import styled from '@emotion/styled';
import { useStudent } from '../../context/StudentContext';
import { useHistory } from 'react-router-dom';
import { getCourseProgress } from '../../utils';
import { useEffect, useState, useRef } from 'react';
import { screenSizes } from 'src/assets/styles/Literals';

const SidebarStyles = styled.div`
  .toggle-button {
    display: none;
    position: fixed;
    bottom: 1rem;
    left: 1rem;
    z-index: 1001;
    background-color: #4caf50;
    color: white;
    border: none;
    border-radius: 50%;
    width: 50px;
    height: 50px;
    cursor: pointer;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    transition: all 0.2s ease;
    font-size: 1.2rem;

    &:hover {
      background-color: #45a049;
      transform: scale(1.05);
    }

    @media (max-width: ${screenSizes.laptop}) {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  p {
    margin: 0;
  }

  .sidebar {
    width: ${({ sidebarWidth }) => `${sidebarWidth}px`};
    padding: 1rem;
    background-color: #f8f9fa;
    border-right: 1px solid #e0e0e0;
    display: flex;
    flex-direction: column;
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.05);
    transition: transform 0.3s ease;
    position: relative;

    @media (max-width: ${screenSizes.laptop}) {
      position: fixed;
      top: 0;
      left: 0;
      height: 100vh;
      z-index: 1000;
      transform: ${({ isOpen }) => (isOpen ? 'translateX(0)' : 'translateX(-100%)')};
      width: 280px;
    }

    .resize-handle {
      position: absolute;
      top: 0;
      right: -5px;
      width: 10px;
      height: 100%;
      cursor: col-resize;
      background-color: transparent;
      z-index: 10;
      display: none;

      @media (min-width: ${screenSizes.laptop}) {
        display: block;
      }

      &:hover {
        background-color: rgba(0, 0, 0, 0.1);
      }

      &:active {
        background-color: rgba(0, 0, 0, 0.2);
      }
    }

    .close-button {
      display: none;
      position: absolute;
      top: 1rem;
      right: 1rem;
      background-color: #f44336;
      color: white;
      border: none;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      cursor: pointer;
      font-size: 1.2rem;
      z-index: 1002;
      transition: all 0.2s ease;

      &:hover {
        background-color: #d32f2f;
        transform: scale(1.05);
      }

      @media (max-width: ${screenSizes.laptop}) {
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    .go-back-button {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 1rem;
      background-color: white;
      border: none;
      border-radius: 8px;
      color: #2c3e50;
      font-size: 0.95rem;
      cursor: pointer;
      transition: all 0.2s ease;
      margin-bottom: 1rem;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
      width: fit-content;

      &:hover {
        background-color: #f8f9fa;
        transform: translateX(-5px);
      }

      &:before {
        content: '←';
        font-size: 1.2rem;
      }
    }

    .title {
      font-family: 'Raleway', sans-serif;
      margin-bottom: 1rem;
      color: #2c3e50;
      font-size: 1.5rem;
      position: relative;
      padding-bottom: 1rem;

      &:after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 50px;
        height: 3px;
        background: linear-gradient(90deg, #4caf50, #45a049);
        border-radius: 2px;
      }
    }

    .progress-container {
      margin-bottom: 1rem;
      padding: 1rem;
      background-color: white;
      border-radius: 12px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);

      .progress-label {
        display: flex;
        justify-content: space-between;
        margin-bottom: 0.5rem;
        font-size: 0.95rem;
        color: #2c3e50;
        font-weight: 500;
      }

      .progress-bar {
        height: 10px;
        background-color: #e9ecef;
        border-radius: 5px;
        overflow: hidden;

        .progress {
          height: 100%;
          background: linear-gradient(90deg, #4caf50, #45a049);
          border-radius: 5px;
          transition: width 0.5s ease;
        }
      }
    }

    .category-list {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
      overflow-y: scroll;
      padding-right: 0.5rem;
      height: 80vh;

      &::-webkit-scrollbar {
        width: 6px;
      }

      &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 3px;
      }

      &::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 3px;
      }

      &::-webkit-scrollbar-thumb:hover {
        background: #a8a8a8;
      }

      .category {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        background-color: white;
        border-radius: 12px;
        padding: 1.25rem;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
        transition: all 0.2s ease;

        .category-title {
          font-size: 1.1rem;
          margin-bottom: 0.75rem;
          color: #2c3e50;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.5rem;

          &:before {
            content: '📚';
            font-size: 1.2rem;
          }
        }

        .lesson {
          text-align: left;
          padding: 0.875rem 1rem;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          background-color: transparent;
          color: #333;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 0.95rem;
          position: relative;
          overflow: hidden;
          font-weight: 400;

          &:disabled {
            cursor: not-allowed;
            color: #9e9e9e;
          }

          &.active {
            background-color: #e3f2fd;
            color: #333;
            font-weight: 500;
          }

          &.completed {
            background-color: #e8f5e9;
            color: #2e7d32;
            font-weight: 500;
          }

          &.locked {
            cursor: not-allowed;
            color: #9e9e9e;

            &:hover {
              background-color: transparent;
              transform: none;
            }
          }

          &:hover:not(:disabled) {
            background-color: #e3f2fd;
            transform: translateX(5px);
          }

          &.completed:hover:not(:disabled) {
            background-color: #c8e6c9;
          }

          .status-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 28px;
            min-height: 28px;
            border-radius: 50%;
            background-color: #e0e0e0;
            color: white;
            font-size: 0.9rem;
            transition: all 0.2s ease;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

            &.status-completed {
              background-color: #4caf50;
            }

            &.status-current {
              background-color: #2196f3;
            }

            &.status-locked {
              background-color: #9e9e9e;
            }
          }

          .lesson-type-icon {
            font-size: 1.1rem;
            margin-left: auto;
            color: #9e9e9e;
            opacity: 1;

            &.type-video {
              color: #e91e63;
            }

            &.type-text {
              color: #2196f3;
            }

            &.type-quiz {
              color: #ff9800;
            }

            &.completed {
              color: #2e7d32;
              opacity: 0.8;
            }
          }
        }
      }
    }
  }

  .overlay {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 999;
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
    visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
    transition: all 0.3s ease;

    @media (max-width: ${screenSizes.laptop}) {
      display: block;
    }
  }
`;

const CourseSidebar = ({
  course,
  currentLesson,
  onLessonSelect,
  progress,
  groupedLessons,
  isOpen,
  onToggle,
}) => {
  const history = useHistory();
  const { student } = useStudent();
  const courseProgress = getCourseProgress(student, course?.id);
  const [sidebarWidth, setSidebarWidth] = useState(340);
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef(null);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (window.innerWidth <= screenSizes.tablet && isOpen) {
        const sidebar = document.querySelector('[data-sidebar]');
        const toggleButton = document.querySelector('[data-toggle]');
        if (sidebar && !sidebar.contains(event.target) && !toggleButton?.contains(event.target)) {
          onToggle();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onToggle]);

  // Close sidebar when screen size changes to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > screenSizes.tablet && isOpen) {
        onToggle();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, onToggle]);

  // Handle sidebar resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;

      const newWidth = e.clientX;
      const minWidth = 280;
      const maxWidth = Math.min(600, window.innerWidth * 0.5);

      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  const isLessonCompleted = (lessonId) => {
    if (!lessonId) return false;
    return courseProgress?.completedLessons?.includes(lessonId) || false;
  };

  const getStatusIcon = (lesson) => {
    if (!lesson || !lesson.id) return { icon: '○', status: 'default' };
    if (isLessonCompleted(lesson.id)) return { icon: '✓', status: 'completed' };
    if (lesson.id === currentLesson?.id) return { icon: '▶', status: 'current' };
    if (lesson.locked) return { icon: '🔒', status: 'locked' };
    return { icon: '○', status: 'default' };
  };

  const getLessonTypeIcon = (type) => {
    switch (type) {
      case 'video':
        return '🎬';
      case 'text':
        return '📖';
      case 'quiz':
        return '✍️';
      default:
        return '📄';
    }
  };

  const handleLessonSelect = (lesson) => {
    onLessonSelect(lesson);
    // Close sidebar on mobile after selecting a lesson
    if (window.innerWidth <= screenSizes.tablet && isOpen) {
      onToggle();
    }
  };

  return (
    <SidebarStyles isOpen={isOpen} sidebarWidth={sidebarWidth}>
      <button className="toggle-button" onClick={onToggle} data-toggle>
        ☰
      </button>
      <div className="sidebar" data-sidebar ref={sidebarRef}>
        <div className="resize-handle" onMouseDown={() => setIsResizing(true)} />
        <button className="close-button" onClick={onToggle}>
          ×
        </button>
        <button className="go-back-button" onClick={() => history.push('/crecerplus')}>
          Volver
        </button>
        <h2 className="title">{course?.title || 'Curso sin título'}</h2>
        <div className="progress-container">
          <div className="progress-label">
            <span>Progreso</span>
            <span>{progress || 0}%</span>
          </div>
          <div className="progress-bar">
            <div className="progress" style={{ width: `${progress || 0}%` }} />
          </div>
        </div>
        <div className="category-list">
          {Object.entries(groupedLessons || {}).map(([category, lessons]) => (
            <div className="category" key={category}>
              <h3 className="category-title">{category}</h3>
              {(lessons || [])
                .filter((lesson) => lesson && typeof lesson === 'object')
                .map((lesson) => ({
                  ...lesson,
                  order: lesson.order || 0,
                  title: lesson.title || 'Sin título',
                  type: lesson.type || 'text',
                  locked: lesson.locked || false,
                }))
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((lesson) => {
                  const { icon, status } = getStatusIcon(lesson);
                  const completed = isLessonCompleted(lesson.id);
                  return (
                    <button
                      key={lesson.id}
                      className={`lesson ${currentLesson?.id === lesson.id ? 'active' : ''} ${
                        completed ? 'completed' : ''
                      } ${lesson.locked ? 'locked' : ''}`}
                      onClick={() => handleLessonSelect(lesson)}
                      disabled={lesson.locked}
                    >
                      <span className={`status-icon status-${status}`}>{icon}</span>
                      <p>{lesson.title}</p>
                      <span
                        className={`lesson-type-icon type-${lesson.type} ${
                          completed ? 'completed' : ''
                        }`}
                      >
                        {getLessonTypeIcon(lesson.type)}
                      </span>
                    </button>
                  );
                })}
            </div>
          ))}
        </div>
      </div>
      <div className="overlay" onClick={onToggle} />
    </SidebarStyles>
  );
};

export { CourseSidebar };
